import React from 'react'
import { observer } from 'mobx-react'
import { useActivation } from 'hooks/mobx-hooks'
import { UpdateProgressionStepDialogVM } from './UpdateProgressionStepDialogVM'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import Button from 'components/Button'
import { useBrandingSettings } from 'hooks/branding'
import { Form, FormGroupContainer, FormRow } from 'components/inputs'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'

interface UpdateProgressionStepDialogProps {
  vm: UpdateProgressionStepDialogVM
}

export default observer(function UpdateProgressionStepDialog({
  vm,
}: UpdateProgressionStepDialogProps) {
  useActivation(vm, [vm.steps])

  const { corporateMode } = useBrandingSettings()

  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.steps, (item, text) =>
        item.display.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.steps],
  )

  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick={vm.update.pending}
      disableCloseOnEsc={vm.update.pending}
    >
      <ModalSheetHeader withCloseButton></ModalSheetHeader>

      <ModalSheetContent>
        {!corporateMode && (
          <ModalSheetContentIllustration
            src={require('assets/images/img_confirmation.svg')}
            alt="Illustration of a finger coloring a checkmark"
          />
        )}

        <ModalSheetContentTitle>
          Update the job's progression
        </ModalSheetContentTitle>

        <ModalSheetContentDescription>
          Select another progression step to override{' '}
          <strong>{vm.jobProgressionVM.currentStepInfo?.title}</strong>
        </ModalSheetContentDescription>

        <ModalSheetContentBlock>
          <Form>
            <FormGroupContainer>
              <FormRow>
                <CustomSelect
                  {...selectProps}
                  placeholder={'Select progression step'}
                  multi={false}
                  data-test="update-job-progression-dialog-dropdown"
                  value={vm.selectedStepOption}
                  onChange={vm.selectProgressionStep}
                  itemToString={(item) => item.display}
                ></CustomSelect>
              </FormRow>
            </FormGroupContainer>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer></ModalSheetFooterSpacer>
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} disabled={vm.update.pending}>
            Close
          </Button>

          <Button
            type="primary"
            onClick={vm.update}
            data-test="update-job-progression-dialog-button"
            loading={vm.update.pending}
            disabled={!vm.selectedStepOption}
          >
            Override
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
